import type { Todo } from "../../domain/entities.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import type { FieldConflict } from "../../infrastructure/sync/automerge.ts";
import { combineConflictValues } from "./productivity.ts";
import { escapeHtml } from "../../ui/text.ts";
import { conflictValueView, type ConflictName } from "./conflict-value-view.ts";
import { createDialogOperations } from "../../ui/dialog-operations.ts";

export interface ConflictControllerCapabilities {
  name?: ConflictName;
  conflicts(taskId: string): Promise<FieldConflict[] | null>;
  resolve(taskId: string, field: keyof Todo, value: unknown): Promise<void>;
  markLocal(): void;
  renderShell(): Promise<void>;
  sync(): void;
  toast(message: string): void;
  icon(name: string): string;
}

export interface ConflictWorkflowCapabilities {
  name?: ConflictName;
  activeRoot(): string | null;
  entities(rootId: string): Promise<StoredEntity[]>;
  actorId(): string;
  conflictValues(entity: StoredEntity, actorId: string): FieldConflict[];
  changeTodo(taskId: string, rootId: string, actorId: string, update: (todo: Todo) => void): Promise<unknown>;
  markLocal(): void;
  renderShell(): Promise<void>;
  sync(): void;
  toast(message: string): void;
  icon(name: string): string;
  openDialog?(taskId: string, capabilities: ConflictControllerCapabilities): Promise<void>;
}

export function conflictFieldName(element: HTMLElement): keyof Todo {
  return element.closest<HTMLElement>("[data-conflict-field]")!.dataset.conflictField as keyof Todo;
}

export function conflictDisplay(value: unknown): string {
  if (typeof value === "string") return value || "(empty)";
  if (value === null || value === undefined) return "None";
  return typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
}

export function conflictLabel(field: keyof Todo): string {
  return String(field).replace(/([A-Z])/g, " $1").replace(/^./, value => value.toUpperCase());
}

export function conflictFieldView(conflict: FieldConflict, name?: ConflictName): string {
  const combinable = ["title", "description"].includes(conflict.field) && conflict.values.every(value => typeof value === "string") || ["assignedTo", "dependencyIds", "tagOrder", "subtasks", "relationships"].includes(conflict.field) && conflict.values.every(Array.isArray) || conflict.field === "tags" && conflict.values.every(value => !!value && typeof value === "object" && !Array.isArray(value));
  return `<section data-conflict-field="${conflict.field}"><header><h3>${escapeHtml(conflictLabel(conflict.field))}</h3>${combinable ? `<button type="button" class="text-button" data-conflict-combine>Combine values — preview</button>` : ""}</header><div class="conflict-values">${conflict.values.map((value, index) => `<article><h4>Version ${index + 1}</h4>${conflictValueView(conflict.field, value, name)}<button type="button" class="button button--soft" data-conflict-choice="${index}" aria-label="Preview version ${index + 1} for ${escapeHtml(conflictLabel(conflict.field))}">Preview this version</button></article>`).join("")}</div><div data-conflict-preview></div></section>`;
}

export async function openConflictDialog(taskId: string, capabilities: ConflictControllerCapabilities): Promise<void> {
  const loaded = await capabilities.conflicts(taskId);
  if (!loaded) return;
  let fields = loaded;
  if (!fields.length) { capabilities.toast("This task has no unresolved conflicts"); return; }
  const dialog = document.createElement("dialog"); dialog.className = "name-dialog conflict-dialog";
  dialog.setAttribute("aria-label", "Resolve task conflicts");
  const operations = createDialogOperations(dialog, capabilities.toast);
  const preview = (field: keyof Todo, choice: number) => {
    const conflict = fields.find(item => item.field === field); if (!conflict) return;
    const value = structuredClone(choice < 0 ? combineConflictValues(field, conflict.values) : conflict.values[choice]);
    const baseline = JSON.stringify(conflict.values);
    dialog.querySelectorAll("[data-conflict-preview]").forEach(element => element.replaceChildren());
    const section = Array.from(dialog.querySelectorAll<HTMLElement>("[data-conflict-field]")).find(element => element.dataset.conflictField === field)!;
    const panel = section.querySelector<HTMLElement>("[data-conflict-preview]")!;
    panel.innerHTML = `<section class="conflict-proposal"><h3>Proposed ${escapeHtml(conflictLabel(field))}</h3>${conflictValueView(field, value, capabilities.name)}<p>Nothing changes until you choose Apply resolution.</p><div class="dialog-actions"><button type="button" class="button button--soft" data-conflict-cancel>Cancel</button><button type="button" class="button button--primary" data-conflict-apply>Apply resolution</button></div></section>`;
    panel.querySelector("[data-conflict-cancel]")!.addEventListener("click", () => { panel.replaceChildren(); section.querySelector<HTMLElement>("[data-conflict-choice]")?.focus(); });
    panel.querySelector("[data-conflict-apply]")!.addEventListener("click", () => { void operations.run(async () => {
      const fresh = (await capabilities.conflicts(taskId))?.find(item => item.field === field);
      if (!fresh || JSON.stringify(fresh.values) !== baseline) throw new Error("This conflict changed. Close and reopen to compare the latest values.");
      await capabilities.resolve(taskId, field, value);
      fields = fields.filter(item => item.field !== field); capabilities.markLocal(); capabilities.sync();
      if (!fields.length) { dialog.close(); capabilities.toast("All conflicts resolved"); await capabilities.renderShell(); return; }
      render(); operations.toast(`${conflictLabel(field)} resolved`);
    }); });
    panel.querySelector<HTMLElement>("[data-conflict-apply]")?.focus();
  };
  const render = () => {
    operations.render(`<div class="share-dialog__header"><div><p class="eyebrow">Conflict resolution</p><h2>Compare competing values</h2><p>Preview a version or combine compatible values, then explicitly apply your choice.</p></div><button type="button" class="icon-button" data-conflict-close aria-label="Close conflicts">${capabilities.icon("close")}</button></div><div class="conflict-fields">${fields.map(field => conflictFieldView(field, capabilities.name)).join("")}</div>`);
    dialog.querySelector("[data-conflict-close]")!.addEventListener("click", () => dialog.close());
    dialog.querySelectorAll<HTMLElement>("[data-conflict-choice]").forEach(button => button.addEventListener("click", () => preview(conflictFieldName(button), Number(button.dataset.conflictChoice))));
    dialog.querySelectorAll<HTMLElement>("[data-conflict-combine]").forEach(button => button.addEventListener("click", () => preview(conflictFieldName(button), -1)));
  };
  render(); document.body.append(dialog); dialog.addEventListener("close", () => { dialog.remove(); void capabilities.renderShell(); }); dialog.showModal();
}

export function createConflictWorkflowController(capabilities: ConflictWorkflowCapabilities) {
  return {
    async open(taskId: string): Promise<void> {
      const rootId = capabilities.activeRoot();
      if (!rootId) return;
      const openDialog = capabilities.openDialog || openConflictDialog;
      await openDialog(taskId, {
        name: capabilities.name,
        conflicts: async id => {
          const entity = (await capabilities.entities(rootId)).find(item => item.id === id);
          return entity ? capabilities.conflictValues(entity, capabilities.actorId()) : null;
        },
        resolve: async (id, field, value) => {
          await capabilities.changeTodo(id, rootId, capabilities.actorId(), todo => { (todo as unknown as Record<string, unknown>)[field] = value; });
        },
        markLocal: capabilities.markLocal,
        renderShell: capabilities.renderShell,
        sync: capabilities.sync,
        toast: capabilities.toast,
        icon: capabilities.icon,
      });
    },
  };
}
